import React, { useEffect, useState } from "react";
import { NavLink, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useApp } from "@/lib/store";
import {
    LayoutDashboard,
    Bot,
    Star,
    Inbox,
    MessageCircle,
    BarChart3,
    Settings as SettingsIcon,
    ChevronDown,
    LogOut,
    MapPin,
    ShieldCheck,
    Gauge,
    Globe,
    Plus,
    Building2,
    Radar,
    Menu,
    X,
} from "lucide-react";
import CommandBar from "@/components/CommandBar";
import BrandMark from "@/components/BrandMark";
import api from "@/lib/api";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
    { path: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "nav-item-home", flag: null },
    { path: "/presence", label: "Presence Analysis", icon: Radar, testid: "nav-item-presence", flag: null },
    { path: "/ai-manager", label: "AI Manager", icon: Bot, testid: "nav-item-ai-manager", flag: "ai_manager" },
    { path: "/audit", label: "Audit", icon: Gauge, testid: "nav-item-audit", flag: "audit" },
    { path: "/seo", label: "SEO & Visibility", icon: Globe, testid: "nav-item-seo", flag: null },
    { path: "/reputation", label: "Reputation", icon: Star, testid: "nav-item-reputation", flag: "reputation" },
    { path: "/inbox", label: "Inbox", icon: MessageCircle, testid: "nav-item-inbox", flag: null },
    { path: "/approvals", label: "Approvals", icon: Inbox, testid: "nav-item-approvals", flag: "approvals" },
    { path: "/reports", label: "Reports", icon: BarChart3, testid: "nav-item-reports", flag: "reports" },
    { path: "/company-profile", label: "Company Profile", icon: Building2, testid: "nav-item-company-profile", flag: null },
    { path: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-item-settings", flag: null },
];

export default function Layout() {
    const {
        user,
        businesses,
        currentBusiness,
        currentLocationId,
        switchBusiness,
        switchLocation,
        logout,
        featureFlags,
    } = useApp();
    const loc = useLocation();
    const nav = useNavigate();
    const [replyMode, setReplyMode] = useState(null);
    const [navOpen, setNavOpen] = useState(false);
    // Close the phone menu after navigating.
    useEffect(() => setNavOpen(false), [loc.pathname]);
    const [googleLive, setGoogleLive] = useState(false);

    // Header badges reflect the real settings for the selected business.
    useEffect(() => {
        if (!currentBusiness?.id) return;
        let alive = true;
        api.autonomy(currentBusiness.id)
            .then((r) => alive && setReplyMode(r?.autonomy?.review_replies || null))
            .catch(() => alive && setReplyMode(null));
        api.integrations(currentBusiness.id)
            .then((r) => {
                const gbp = (r?.integrations || r?.connections || []).find(
                    (c) => c.provider === "google_business_profile",
                );
                if (alive) setGoogleLive(gbp?.status === "connected" && gbp?.mode === "live");
            })
            .catch(() => alive && setGoogleLive(false));
        const onChange = (e) => setReplyMode(e.detail?.review_replies || null);
        window.addEventListener("autonomy-changed", onChange);
        return () => {
            alive = false;
            window.removeEventListener("autonomy-changed", onChange);
        };
    }, [currentBusiness?.id]);

    if (!user) return null;
    const MODE_BADGE = {
        SUGGEST: { label: "Suggest mode", cls: "border-sky-500/30 bg-sky-500/10 text-sky-200" },
        APPROVAL: { label: "Approval mode", cls: "border-amber-500/30 bg-amber-500/10 text-amber-200" },
        AUTOPILOT: { label: "Autopilot", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" },
    };
    const modeBadge = MODE_BADGE[replyMode];
    const visibleNav = NAV.filter(
        (n) => !n.flag || featureFlags?.[n.flag] !== false,
    );
    const initials = (user.name || user.email || "?")
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <div className="min-h-screen grain">
            {navOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/60 lg:hidden"
                    onClick={() => setNavOpen(false)}
                    aria-hidden="true"
                />
            )}
            {/* Sidebar — fixed on desktop, slide-in menu on phones and tablets */}
            <aside
                data-testid="app-sidebar"
                className={`fixed inset-y-0 left-0 w-64 z-50 border-r border-white/5 bg-[#0B0F19]/95 backdrop-blur-xl flex flex-col justify-between p-4 overflow-y-auto transition-transform lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}
            >
                <div>
                    <div className="px-2 py-3 flex items-center justify-between">
                        <BrandMark />
                        <button
                            className="lg:hidden h-8 w-8 flex items-center justify-center text-slate-400"
                            onClick={() => setNavOpen(false)}
                            aria-label="Close menu"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <nav className="mt-6 flex flex-col gap-1">
                        {visibleNav.map((n) => {
                            const active = loc.pathname.startsWith(n.path);
                            const Icon = n.icon;
                            return (
                                <NavLink
                                    key={n.path}
                                    to={n.path}
                                    data-testid={n.testid}
                                    className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                                        active
                                            ? "bg-indigo-500/15 text-indigo-100 ring-1 ring-inset ring-indigo-500/30"
                                            : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.04]"
                                    }`}
                                >
                                    <Icon
                                        className={`h-4 w-4 ${
                                            active
                                                ? "text-indigo-300"
                                                : "text-slate-500 group-hover:text-slate-300"
                                        }`}
                                    />
                                    {n.label}
                                </NavLink>
                            );
                        })}
                    </nav>
                </div>
                <div className="border-t border-white/5 pt-4">
                    <div className="flex items-center gap-3 px-2 pb-2">
                        <Avatar className="h-8 w-8">
                            {user.picture && <AvatarImage src={user.picture} />}
                            <AvatarFallback className="bg-indigo-500/20 text-indigo-100 text-xs font-semibold">
                                {initials}
                            </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold text-slate-200 truncate">
                                {user.name}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">
                                {user.email}
                            </div>
                        </div>
                        <button
                            onClick={logout}
                            data-testid="logout-button"
                            className="text-slate-500 hover:text-rose-300 transition-colors"
                            title="Sign out"
                        >
                            <LogOut className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Top bar */}
            <header
                data-testid="app-topbar"
                className="lg:ml-64 sticky top-0 z-30 h-16 border-b border-white/5 bg-[#0B0F19]/85 backdrop-blur-xl px-3 sm:px-6 flex items-center justify-between gap-2"
            >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <button
                        className="lg:hidden h-9 w-9 shrink-0 flex items-center justify-center rounded-xl border border-white/10 text-slate-300"
                        onClick={() => setNavOpen(true)}
                        aria-label="Open menu"
                        data-testid="app-menu-toggle"
                    >
                        <Menu className="h-4 w-4" />
                    </button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="tenant-switcher-dropdown"
                                className="flex items-center gap-2 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 px-3 py-1.5 text-sm text-slate-100 transition-colors"
                            >
                                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                                <span className="font-semibold truncate max-w-[9rem] sm:max-w-none">
                                    {currentBusiness?.name || "Select business"}
                                </span>
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-64">
                            <DropdownMenuLabel>Your businesses</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {businesses.map((b) => (
                                <DropdownMenuItem
                                    key={b.id}
                                    onClick={() => switchBusiness(b.id)}
                                    data-testid={`tenant-option-${b.id}`}
                                    className={
                                        currentBusiness?.id === b.id
                                            ? "bg-indigo-500/10"
                                            : ""
                                    }
                                >
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium">
                                            {b.name}
                                        </span>
                                        <span className="text-[11px] text-slate-500">
                                            {b.category}
                                        </span>
                                    </div>
                                </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => nav("/onboarding")}
                                data-testid="add-company-button"
                                className="text-indigo-300 focus:text-indigo-200"
                            >
                                <Plus className="h-4 w-4 mr-1.5" /> Add company
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="location-switcher-select"
                                className="hidden sm:flex items-center gap-2 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 px-3 py-1.5 text-sm text-slate-300 transition-colors"
                            >
                                <MapPin className="h-3.5 w-3.5 text-slate-500" />
                                <span className="text-sm">
                                    {currentLocationId
                                        ? currentBusiness?.locations?.find(
                                              (l) => l.id === currentLocationId,
                                          )?.name || "Location"
                                        : "All locations"}
                                </span>
                                <ChevronDown className="h-4 w-4 text-slate-500" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuItem
                                onClick={() => switchLocation(null)}
                                data-testid="location-option-all"
                            >
                                All locations
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {currentBusiness?.locations?.map((l) => (
                                <DropdownMenuItem
                                    key={l.id}
                                    onClick={() => switchLocation(l.id)}
                                    data-testid={`location-option-${l.id}`}
                                >
                                    {l.name}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="flex items-center gap-3">
                    {modeBadge && (
                        <Badge
                            variant="outline"
                            data-testid="autopilot-status-pill"
                            title="How review replies are handled — change it in Settings"
                            onClick={() => nav("/settings?tab=autonomy")}
                            className={`${modeBadge.cls} gap-1.5 cursor-pointer hidden sm:inline-flex`}
                        >
                            <ShieldCheck className="h-3 w-3" /> {modeBadge.label}
                        </Badge>
                    )}
                    <Badge
                        variant="outline"
                        data-testid="google-sync-status"
                        className={`hidden md:inline-flex ${googleLive ? "border-emerald-500/30 text-emerald-200" : "border-white/10 text-slate-400"}`}
                    >
                        {googleLive ? "Google reviews live" : "Sample reviews — Google Business not connected"}
                    </Badge>
                </div>
            </header>

            {/* Main */}
            <main
                data-testid="app-main"
                className="lg:ml-64 px-4 sm:px-8 py-6 sm:py-8 pb-40 min-h-[calc(100vh-4rem)]"
            >
                <Outlet />
            </main>

            {/* Persistent AI command bar (hidden on AI Manager page — replaced by full chat there) */}
            {featureFlags?.ai_manager !== false &&
                !loc.pathname.startsWith("/ai-manager") && <CommandBar />}
            <Toaster richColors closeButton position="top-right" />
        </div>
    );
}
